import { useQuery } from '@tanstack/react-query';

import axios, { parseAxiosError } from '@/portainer/services/axios';
import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { withGlobalError } from '@/react-tools/react-query';
import { EnvironmentId } from '@/react/portainer/environments/types';

import { Registry } from '../types/registry';

import { buildUrl } from './build-url';

// Internal axios functions (unexported)
async function ping(registryId: Registry['Id'], endpointId: EnvironmentId) {
  try {
    const { data } = await axios.get(`${buildUrl(registryId)}/v2/`, {
      params: { endpointId },
      headers: {
        Accept: 'application/json',
      },
    });
    return data;
  } catch (e) {
    throw parseAxiosError(e as Error);
  }
}

async function pingWithForceNew(
  registryId: Registry['Id'],
  endpointId: EnvironmentId
) {
  try {
    const { data } = await axios.get(`${buildUrl(registryId)}/v2/`, {
      params: { endpointId },
      headers: {
        Accept: 'application/json',
        'X-RegistryManagement-ForceNew': '1',
      },
    });
    return data;
  } catch (e) {
    throw parseAxiosError(e as Error);
  }
}

// React Query hooks (exported)
export function useRegistryPing(
  registryId: Registry['Id'],
  options?: { enabled?: boolean }
) {
  const environmentId = useEnvironmentId(false);

  return useQuery(
    ['environment', environmentId, 'registries', registryId, 'ping'],
    () => ping(registryId, environmentId),
    {
      ...options,
      ...withGlobalError(),
    }
  );
}

export function useRegistryPingWithForceNew(
  registryId: Registry['Id'],
  endpointId: EnvironmentId,
  options?: { enabled?: boolean }
) {
  return useQuery(
    ['environment', endpointId, 'registries', registryId, 'ping', 'forceNew'],
    () => pingWithForceNew(registryId, endpointId),
    {
      ...options,
      ...withGlobalError(),
    }
  );
}

// Export the axios functions for use in AngularJS services
export { ping, pingWithForceNew };
